/**
 * The frame around every screen: the sidebar, the two bars above the content, the command palette
 * and the small notices — the reference's shell, made to work and to fold onto a phone.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { ChartPie, ChevronDown, ChevronsUpDown, CircleHelp, Ellipsis, LogOut, Menu as MenuIcon, MessageSquareDashed, PanelLeft, Plus, RotateCw, Search, Share, Users } from 'lucide-react'
import { Burst, Caret, MemoryClock, Nut, Steps, Tiles, UserAvatar, WorkspaceMark } from './icons'
import { Calculator } from '../components/calculator/Calculator'
import { SymbolModal } from '../components/symbol/SymbolModal'
import { Guard } from './Guard'
import { AddAgent, AddGroup, AgentsView, Inspector, fillComposer, useMedia, useTarget } from './Agents'
import { TINTS, type Agent, type Folder, type Space } from './data'
import { AppsBar, AppsView } from './Apps'
import { ChatsBar, ChatsView } from './Chats'
import { PortfolioBar, PortfolioView } from './Portfolio'
import { SettingsBar, SettingsView } from './Settings'
import { WorkersBar, WorkersView } from './Work'
import { AskHost, Btn, Dialog, Dot, Field, Glyph, I, IconBtn, KeyCap, Menu, PopHost, Rule, Sheet, Stack, cx, inputCls, menuOn, popAt, type Icon, type Palette } from './kit'
import { Logo } from './logos'
import { agentMenu, barMenu, folderAgents, folderMenu, groupMenu, newFolder } from './menus'
import { useTabs } from './Page'
import { useDemo, type View } from './store'

/** The face of whoever the agents screen is about: an agent's glyph, or a group's members (one glyph where a row of icons must line up). */
function Face({ one }: { one?: boolean }) {
  const { agent, members } = useTarget()
  return agent || one ? <Glyph palette={(agent ?? members[0])?.palette ?? 'syntax'} /> : <Stack palettes={members.map((a) => a.palette)} />
}

const TITLES: Record<View, { label: string; icon: Icon }> = {
  agents: { label: 'Agents', icon: Search },
  chats: { label: 'Temporary chats', icon: MessageSquareDashed },
  portfolio: { label: 'Portfolio', icon: ChartPie },
  workers: { label: 'Workers', icon: Steps },
  apps: { label: 'Apps', icon: Tiles },
  settings: { label: 'Settings', icon: Nut },
}

/* ── sidebar ─────────────────────────────────────────────────────────── */

function NavItem({ view, icon, glyph, label, keys, badge, soon }: { view?: View; icon?: Icon; glyph?: ReactNode; label: string; keys?: string[]; badge?: number; soon?: boolean }) {
  const active = useDemo((s) => s.view === view)
  const go = useDemo((s) => s.go)
  return (
    <button
      type="button"
      disabled={soon}
      onClick={() => view && go(view)}
      className={cx('flex h-7 w-full items-center rounded-[8px] pl-[6px] text-left transition-colors duration-150', active ? 'bg-ws-panel font-medium text-ws-fg' : soon ? 'text-ws-fg-4' : 'text-ws-fg-2 hover:bg-ws-hover hover:text-ws-fg', keys ? 'pr-2' : 'pr-3')}
    >
      {glyph ?? (icon && <I icon={icon} className={soon ? 'text-[#5c5c5c]' : active ? 'text-ws-fg-2' : 'text-ws-icon'} />)}
      <span className="ml-2 truncate">{label}</span>
      {keys && (
        <span className="ml-auto hidden gap-[2px] md:flex">
          {keys.map((k) => (
            <KeyCap key={k} sunk={active}>
              {k}
            </KeyCap>
          ))}
        </span>
      )}
      {badge != null && badge > 0 && <span className="ml-auto flex h-4 min-w-4 items-center justify-center rounded-[4px] bg-white px-[3px] text-[10.5px] font-medium leading-none tracking-normal text-black">{badge}</span>}
      {soon && <span className="ml-auto text-[10px] leading-none tracking-normal text-ws-fg-5">Soon</span>}
    </button>
  )
}

function Group({ label, children, className }: { label: string; children: ReactNode; className?: string }) {
  const [open, setOpen] = useState(true)
  return (
    <div className={cx('flex flex-col gap-1', className)}>
      <button type="button" onClick={() => setOpen(!open)} className="flex h-7 items-center gap-2 pl-[6px] text-[12px] tracking-normal text-ws-label transition-colors hover:text-ws-fg-2" aria-expanded={open}>
        {label}
        <span className={cx('transition-transform duration-150', !open && '-rotate-90')}>
          <Caret />
        </span>
      </button>
      {open && children}
    </div>
  )
}

/** A workspace's mark: the product's own for the first one, the workspace's colours for the rest. */
export const SpaceMark = ({ space, size = 17 }: { space?: Space; size?: number }) => (space?.tint ? <span aria-hidden className="block shrink-0 rounded-[5px]" style={{ width: size, height: size, background: space.tint }} /> : <WorkspaceMark size={size} />)

/** Which workspace you are in, and the way to another one. */
function SpaceSwitch() {
  const spaces = useDemo((s) => s.spaces)
  const spaceId = useDemo((s) => s.spaceId)
  const agents = useDemo((s) => s.agents)
  const switchSpace = useDemo((s) => s.switchSpace)
  const here = spaces.find((x) => x.id === spaceId)
  const about = (id: string) => (id === spaceId ? `${agents.length} ${agents.length === 1 ? 'agent' : 'agents'}` : '')
  return (
    <Menu
      width={312}
      title="Workspaces"
      items={[
        ...spaces.map((x) => ({ label: x.name, icon: <SpaceMark space={x} size={16} />, hint: about(x.id), checked: x.id === spaceId, onSelect: () => switchSpace(x.id) })),
        'rule' as const,
        { label: 'New workspace', icon: <I icon={Plus} className="text-ws-icon" />, onSelect: () => useDemo.getState().set({ newSpace: true }) },
        { label: 'Workspace settings', icon: <I icon={Nut} className="text-ws-icon" />, onSelect: () => (useDemo.getState().go('settings'), useTabs.getState().set({ settings: 'workspace' })) },
      ]}
      trigger={() => (
        <button type="button" className="flex h-8 min-w-0 max-w-[156px] items-center rounded-[8px] bg-ws-panel pl-[6px] pr-2 font-medium text-ws-fg transition-colors duration-150 hover:bg-ws-raise">
          <SpaceMark space={here} />
          <span className="ml-2 truncate">{here?.name}</span>
          <ChevronsUpDown size={12} strokeWidth={2.2} className="ml-[7px] shrink-0 text-ws-icon" />
        </button>
      )}
    />
  )
}

/** A new workspace needs only a name and a mark: it starts on your AI account, with nothing else connected. */
function AddSpace() {
  const open = useDemo((s) => s.newSpace)
  const add = useDemo((s) => s.addSpace)
  const setS = useDemo((s) => s.set)
  const [name, setName] = useState('')
  const [tint, setTint] = useState(TINTS[1])
  useEffect(() => {
    if (open) (setName(''), setTint(TINTS[1]))
  }, [open])
  const close = () => setS({ newSpace: false })
  return (
    <Dialog
      open={open}
      onClose={close}
      title="New workspace"
      width={440}
      footer={
        <>
          <Btn variant="outline" onPanel onClick={close}>
            Cancel
          </Btn>
          <Btn variant="white" disabled={!name.trim()} onClick={() => (add({ name: name.trim(), tint }), close())}>
            Create
          </Btn>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Name">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Şirket, aile, bir proje…" className={inputCls} autoFocus />
        </Field>
        <Field label="Mark">
          <div className="flex gap-2">
            {TINTS.map((x) => (
              <button key={x} type="button" aria-label="Mark colour" aria-pressed={tint === x} onClick={() => setTint(x)} className={cx('flex h-8 w-8 items-center justify-center rounded-[8px] border transition-colors duration-150', tint === x ? 'border-ws-focus bg-ws-panel' : 'border-ws-line hover:bg-ws-raise')}>
                <span className="block h-4 w-4 rounded-[5px]" style={{ background: x }} />
              </button>
            ))}
          </div>
        </Field>
        <div className="divide-y divide-ws-hair rounded-[8px] border border-ws-line text-[13px] tracking-normal">
          <div className="flex min-h-10 items-center gap-3 px-3 py-[6px]">
            <Logo name="grok" size={20} />
            <span className="min-w-0 flex-1 text-ws-fg">Runs on your Grok plan</span>
            <span className="text-[12px] text-ws-fg-3">Required</span>
          </div>
          <div className="flex min-h-10 items-center gap-3 px-3 py-[6px]">
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-[5px] border border-dashed border-ws-rule" />
            <span className="min-w-0 flex-1 text-ws-fg-2">No broker</span>
            <span className="text-[12px] text-ws-fg-3">Add one in Apps, or never</span>
          </div>
        </div>
        <p className="m-0 text-[12px] leading-[18px] tracking-normal text-ws-fg-3">It gets its own agents, its own memory and its own connections. What agents know about you comes along; nothing else does.</p>
      </div>
    </Dialog>
  )
}

function SidebarBody() {
  const setS = useDemo((s) => s.set)
  const who = useDemo((s) => s.user?.name || s.user?.username || '')
  return (
    <>
      <div className="flex h-12 shrink-0 items-center pl-2 pr-4">
        <SpaceSwitch />
        <IconBtn icon={PanelLeft} size={15} label="Hide the sidebar" className="-mr-2 ml-auto hidden md:flex" onClick={() => setS({ navHidden: true })} />
      </div>

      <div className="px-2 pt-[11px]">
        <button type="button" onClick={() => setS({ palette: true, navOpen: false })} className="flex h-[29px] w-full items-center rounded-[8px] border border-ws-hair pl-[5px] pr-[7px] text-ws-fg-3 transition-colors duration-150 hover:border-ws-edge hover:text-ws-fg-2">
          <I icon={Search} className="text-ws-icon" />
          <span className="ml-2 tracking-normal">Search</span>
          <span className="ml-auto flex gap-[2px]">
            <KeyCap>⌘</KeyCap>
            <KeyCap>K</KeyCap>
          </span>
        </button>
      </div>

      <nav className="mt-5 flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto px-2">
        <NavItem view="agents" glyph={<Face one />} label="Agents" keys={['⌘', 'Y']} />
        <NavItem view="chats" icon={MessageSquareDashed} label="Temporary chats" />
        <Group label="Desk" className="mt-[11px]">
          <NavItem view="portfolio" icon={ChartPie} label="Portfolio" />
          <NavItem view="workers" icon={Steps} label="Workers" />
        </Group>
      </nav>

      <div className="flex shrink-0 flex-col gap-1 px-2 pb-[max(6px,env(safe-area-inset-bottom))] pt-2">
        <Group label="Spaces">
          <NavItem view="apps" icon={Tiles} label="Apps" />
          <NavItem view="settings" icon={Nut} label="Settings" />
        </Group>
        <div className="mt-[10px] flex h-8 items-center pl-[5px]">
          <UserAvatar size={17} />
          <span className="ml-2 min-w-0 truncate font-medium tracking-[-0.012em] text-ws-fg">{who}</span>
          <Menu
            side="top"
            align="right"
            className="ml-auto"
            items={[
              { label: 'Settings', onSelect: () => useDemo.getState().go('settings') },
              { label: 'Apps', onSelect: () => useDemo.getState().go('apps') },
              'rule',
              { label: 'Sign out', icon: <I icon={LogOut} className="text-ws-icon" />, onSelect: () => void useDemo.getState().logout() },
            ]}
            trigger={() => <IconBtn icon={Ellipsis} size={18} label="Account" className="text-[#c4c4c4]" />}
          />
        </div>
      </div>
    </>
  )
}

function Sidebar() {
  const hidden = useDemo((s) => s.navHidden)
  const open = useDemo((s) => s.navOpen)
  const setS = useDemo((s) => s.set)
  return (
    <>
      {!hidden && (
        <aside className="hidden w-[208px] shrink-0 flex-col border-r border-ws-line md:flex">
          <SidebarBody />
        </aside>
      )}
      <Sheet open={open} side="left" onClose={() => setS({ navOpen: false })} className="flex w-[280px] max-w-[86vw] flex-col border-r border-ws-line md:hidden">
        <SidebarBody />
      </Sheet>
    </>
  )
}

/* ── the bars above the content ──────────────────────────────────────── */

function TopBar() {
  const view = useDemo((s) => s.view)
  const agents = useDemo((s) => s.agents)
  const { agent, name } = useTarget()
  const hidden = useDemo((s) => s.navHidden)
  const setS = useDemo((s) => s.set)
  const say = useDemo((s) => s.say)
  const newTemp = useDemo((s) => s.newTemp)
  const openAgent = useDemo((s) => s.openAgent)
  const t = TITLES[view]
  const working = agents.filter((a) => a.busy)
  return (
    <header className="flex h-12 shrink-0 items-center border-b border-ws-line pl-2 pr-2 md:pl-4 md:pr-4">
      <IconBtn icon={MenuIcon} label="Menu" className="md:hidden" onClick={() => setS({ navOpen: true })} />
      {hidden && <IconBtn icon={PanelLeft} size={15} label="Show the sidebar" className="-ml-2 mr-2 hidden md:flex" onClick={() => setS({ navHidden: false })} />}
      <span className="ml-1 flex min-w-0 items-center md:ml-0">
        {view === 'agents' && name ? <Face /> : <I icon={t.icon} className="text-ws-icon" />}
        <span className="ml-2 truncate font-medium text-ws-fg">{view === 'agents' && name ? `Ask ${name}` : t.label}</span>
      </span>

      <div className="ml-auto flex shrink-0 items-center">
        <div className="hidden h-[30px] items-center rounded-[8px] border border-ws-line bg-ws-well sm:flex">
          <button type="button" onClick={() => newTemp()} className="flex h-full items-center gap-2 rounded-l-[7px] pl-[7px] pr-[10px] text-ws-fg-2 transition-colors duration-150 hover:bg-ws-hover hover:text-ws-fg">
            <I icon={Burst} className="text-ws-icon" />
            Quick Chat
          </button>
          <span className="w-px self-stretch bg-ws-hair" />
          <Menu
            align="right"
            width={260}
            items={[
              { label: 'New temporary chat', hint: 'no memory', onSelect: () => newTemp() },
              { label: 'All temporary chats', onSelect: () => useDemo.getState().go('chats') },
              'rule',
              ...agents.map((a) => ({ label: `Ask ${a.name}`, icon: <Glyph palette={a.palette} />, onSelect: () => openAgent(a.id) })),
            ]}
            trigger={() => (
              <span className="flex h-[28px] w-[27px] items-center justify-center rounded-r-[7px] transition-colors duration-150 hover:bg-ws-hover">
                <ChevronDown size={14} strokeWidth={2} className="text-ws-icon" />
              </span>
            )}
          />
        </div>
        <IconBtn icon={Burst} label="Quick chat" className="sm:hidden" onClick={() => newTemp()} />
        {view === 'agents' && agent && (
          <>
            <Btn icon={MemoryClock} className="ml-2 hidden lg:flex" onClick={() => setS({ panel: 'memory' })}>
              Memory
            </Btn>
            <IconBtn icon={MemoryClock} label="Memory" className="lg:hidden" onClick={() => setS({ panel: 'memory' })} />
          </>
        )}
        <Btn
          icon={Share}
          className="ml-[10px] hidden lg:flex"
          onClick={() => {
            void navigator.clipboard?.writeText(location.href).catch(() => undefined)
            say('Link copied')
          }}
        >
          Share
        </Btn>
        <Rule className="ml-[10px] hidden lg:block" />
        <Menu
          align="right"
          width={280}
          className="ml-[10px] hidden lg:block"
          title={working.length ? `${working.length} working now` : 'Everyone is free'}
          items={agents.map((a) => ({ label: a.name, icon: <Glyph palette={a.palette} />, hint: a.busy ? 'busy' : 'free', onSelect: () => openAgent(a.id) }))}
          trigger={() => (
            <span className="flex h-8 items-center rounded-[8px] px-1 transition-colors duration-150 hover:bg-ws-hover" title="The team">
              <Stack palettes={agents.map((a) => a.palette)} max={4} />
            </span>
          )}
        />
        <Rule className="ml-2 hidden lg:block" />
        <Menu
          align="right"
          width={260}
          className="ml-2 hidden md:block"
          title="Shortcuts"
          items={[
            { label: 'Search and commands', hint: '⌘K', onSelect: () => setS({ palette: true }) },
            { label: 'Agents', hint: '⌘Y', onSelect: () => useDemo.getState().go('agents') },
            { label: 'All shortcuts', onSelect: () => (useDemo.getState().go('settings'), useTabs.getState().set({ settings: 'shortcuts' })) },
          ]}
          trigger={() => <IconBtn icon={CircleHelp} label="Help" />}
        />
      </div>
    </header>
  )
}

/** A row that scrolls sideways: its cut edge fades instead of slicing a tab, and the chosen tab is always in view. */
function useStrip(chosen: string, count: number) {
  const ref = useRef<HTMLDivElement>(null)
  const [cut, setCut] = useState({ left: false, right: false })
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const measure = () => {
      const left = el.scrollLeft > 1
      const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1
      setCut((c) => (c.left === left && c.right === right ? c : { left, right }))
    }
    measure()
    el.addEventListener('scroll', measure, { passive: true })
    const watch = new ResizeObserver(measure)
    watch.observe(el)
    for (const c of el.children) watch.observe(c)
    return () => (el.removeEventListener('scroll', measure), watch.disconnect())
  }, [count])
  useEffect(() => {
    const show = () => ref.current?.querySelector('[aria-current=true]')?.scrollIntoView({ inline: 'nearest', block: 'nearest' })
    show()
    const t = window.setTimeout(show, 240) // (again once a folder has finished widening)
    return () => window.clearTimeout(t)
  }, [chosen])
  const fade = cut.left && cut.right ? 'linear-gradient(to right, transparent, #000 28px, #000 calc(100% - 28px), transparent)' : cut.right ? 'linear-gradient(to right, #000 calc(100% - 28px), transparent)' : cut.left ? 'linear-gradient(to right, transparent, #000 28px)' : undefined
  return { ref, style: { maskImage: fade, WebkitMaskImage: fade } }
}

const TAB = 'flex h-8 shrink-0 select-none items-center gap-2 rounded-[8px] pl-2 pr-3 tracking-[-0.02em] transition-colors duration-150 [-webkit-touch-callout:none]'
const tabCls = (on: boolean) => cx(TAB, on ? 'bg-ws-panel font-medium text-ws-fg' : 'text-ws-fg-2 hover:bg-ws-hover hover:text-ws-fg')

/**
 * A folder in the tab row. Closed, it is one tab that looks like a group chat's: its agents' faces and
 * its name. Pressed, it widens in place and its agents stand beside it. It folds again at the next
 * press anywhere else — unless one of its agents is the one you are talking to.
 */
function FolderTab({ folder, members, chosen, waiting, mark }: { folder: Folder; members: Agent[]; chosen: string; waiting: boolean; mark: (key: string, on: string) => ReactNode }) {
  const pressed = useDemo((s) => s.openFolder === folder.id)
  const openAgent = useDemo((s) => s.openAgent)
  const setS = useDemo((s) => s.set)
  const ref = useRef<HTMLDivElement>(null)
  const open = pressed || members.some((a) => a.id === chosen)
  useEffect(() => {
    if (!pressed) return
    const down = (e: PointerEvent) => {
      const t = e.target as Element
      if (!ref.current?.contains(t) && !t.closest?.('[role=dialog],[data-pop]')) setS({ openFolder: null })
    }
    const key = (e: KeyboardEvent) => e.key === 'Escape' && setS({ openFolder: null })
    document.addEventListener('pointerdown', down)
    document.addEventListener('keydown', key)
    return () => (document.removeEventListener('pointerdown', down), document.removeEventListener('keydown', key))
  }, [pressed, setS])
  // Once it has widened, all of it is in view (the row scrolls when there are many tabs).
  useEffect(() => {
    if (!open) return
    const t = window.setTimeout(() => ref.current?.scrollIntoView({ inline: 'nearest', block: 'nearest' }), 240)
    return () => window.clearTimeout(t)
  }, [open])
  const inner = (on: boolean) => cx('flex h-[26px] shrink-0 select-none items-center gap-[6px] rounded-[5px] pl-[6px] pr-2 tracking-[-0.02em] transition-colors duration-150 [-webkit-touch-callout:none]', on ? 'bg-ws-panel font-medium text-ws-fg' : 'text-ws-fg-2 hover:bg-ws-hover hover:text-ws-fg')
  const small = 'flex h-[26px] shrink-0 items-center justify-center rounded-[5px] transition-colors duration-150 hover:bg-ws-hover hover:text-ws-fg'
  return (
    <div ref={ref} className={cx('flex h-8 shrink-0 items-center rounded-[8px] transition-[background-color,box-shadow] duration-200', open ? 'bg-ws-well shadow-[inset_0_0_0_1px_var(--color-ws-line)]' : 'shadow-[inset_0_0_0_1px_transparent]')}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setS({ openFolder: pressed ? null : folder.id })}
        {...menuOn(() => folderMenu(folder.id))}
        className={cx('flex h-8 shrink-0 select-none items-center rounded-[8px] pl-2 pr-3 tracking-[-0.02em] text-ws-fg-2 transition-colors duration-150 [-webkit-touch-callout:none] hover:text-ws-fg', !open && 'hover:bg-ws-hover')}
      >
        {/* closed, the faces are stacked here; open, they stand beside the name one by one */}
        <span className="grid transition-[grid-template-columns,opacity] duration-200 ease-out" style={{ gridTemplateColumns: open ? '0fr' : '1fr', opacity: open ? 0 : 1 }}>
          <span className="flex min-w-0 overflow-x-clip pr-2">
            <span className="relative flex">
              <Stack palettes={members.map((a) => a.palette)} dim pulse={members.map((a) => !!a.busy)} />
              {waiting && <span className="absolute -right-[3px] -top-[3px] h-[7px] w-[7px] rounded-full border-[1.5px] border-ws-bg bg-white" />}
            </span>
          </span>
        </span>
        {folder.name}
      </button>
      <div className="grid transition-[grid-template-columns] duration-200 ease-out" style={{ gridTemplateColumns: open ? '1fr' : '0fr' }}>
        <div className="flex min-w-0 items-center gap-1 overflow-hidden" inert={!open}>
          <span className="-ml-[3px] mr-[5px] h-4 w-px shrink-0 bg-ws-line" />
          {members.map((a) => (
            <button key={a.id} type="button" aria-current={a.id === chosen} onClick={() => openAgent(a.id)} {...menuOn(() => agentMenu(a.id))} className={inner(a.id === chosen)}>
              <span className={cx('relative flex', a.busy && 'animate-ws-pulse')}>
                <Glyph palette={a.palette} dim={a.id !== chosen} />
                {mark(a.id, 'border-ws-well')}
              </span>
              {a.name}
            </button>
          ))}
          {members.length === 0 && (
            <button type="button" onClick={(e) => popAt(e.currentTarget, () => folderAgents(folder.id))} className={cx(small, 'whitespace-nowrap px-2 text-ws-fg-3')}>
              Add agents
            </button>
          )}
          <button type="button" aria-label="Folder options" title="Folder options" onClick={(e) => popAt(e.currentTarget, () => folderMenu(folder.id))} className={cx(small, 'mr-[3px] w-[26px] text-ws-fg-4')}>
            <Ellipsis size={14} strokeWidth={2} />
          </button>
        </div>
      </div>
    </div>
  )
}

function AgentTabs() {
  const agents = useDemo((s) => s.agents)
  const folders = useDemo((s) => s.folders)
  const groups = useDemo((s) => s.groups)
  const unread = useDemo((s) => s.unread)
  const { id, agent, group } = useTarget()
  const newTemp = useDemo((s) => s.newTemp)
  const session = useDemo((s) => s.current[id])
  const openAgent = useDemo((s) => s.openAgent)
  const openSession = useDemo((s) => s.openSession)
  const setS = useDemo((s) => s.set)
  // An answer left while you were elsewhere: a dot on the face, so the tab keeps its width.
  const mark = (key: string, on: string) => unread[key] && key !== id && <span className={cx('absolute -right-[3px] -top-[3px] h-[7px] w-[7px] rounded-full border-[1.5px] bg-white', on)} />
  const palette = (m: string): Palette => agents.find((a) => a.id === m)?.palette ?? 'syntax'
  const strip = useStrip(id, agents.length + groups.length + folders.length)
  // The row, in a fixed order: pinned agents, the other loose agents, your folders, your group chats.
  // A hidden agent is left out unless it is the one you are talking to.
  const seen = agents.filter((a) => !a.hidden || a.id === id)
  const loose = seen.filter((a) => !folders.some((f) => f.members.includes(a.id)))
  const pinned = loose.filter((a) => a.pinned)
  const agentTab = (a: Agent) => (
    <button key={a.id} type="button" aria-current={a.id === id} onClick={() => openAgent(a.id)} {...menuOn(() => agentMenu(a.id))} className={tabCls(a.id === id)}>
      {/* its face breathes while it works */}
      <span className={cx('relative flex', a.busy && 'animate-ws-pulse')}>
        <Glyph palette={a.palette} dim={a.id !== id} />
        {mark(a.id, 'border-ws-bg')}
      </span>
      {a.name}
    </button>
  )
  const settings = () => setS({ panel: 'settings', routineId: null })
  return (
    <div className="flex h-12 shrink-0 items-center border-b border-ws-line pl-2 pr-2 md:pr-4" onContextMenu={(e) => (e.preventDefault(), popAt(e, barMenu))}>
      <div ref={strip.ref} style={strip.style} className="ws-noscroll flex min-w-0 items-center gap-2 overflow-x-auto">
        {pinned.map(agentTab)}
        {pinned.length > 0 && pinned.length < loose.length && <Rule />}
        {loose.filter((a) => !a.pinned).map(agentTab)}
        {folders.map((f) => {
          const members = f.members.map((m) => seen.find((x) => x.id === m)).filter((x): x is Agent => !!x)
          return <FolderTab key={f.id} folder={f} members={members} chosen={id} waiting={members.some((m) => unread[m.id])} mark={mark} />
        })}
        {groups.length > 0 && <Rule />}
        {groups.map((g) => (
          <button key={g.id} type="button" aria-current={g.id === id} onClick={() => openAgent(g.id)} {...menuOn(() => groupMenu(g.id))} className={tabCls(g.id === id)}>
            <span className="relative flex">
              <Stack palettes={g.members.map(palette)} on={g.id === id ? 'bg-ws-panel' : 'bg-ws-bg'} dim={g.id !== id} />
              {mark(g.id, 'border-ws-bg')}
            </span>
            {g.name}
          </button>
        ))}
      </div>
      <Rule className="ml-[8px]" />
      <Menu
        width={150}
        className="ml-2 shrink-0"
        items={[
          { label: 'Agent', onSelect: () => setS({ addAgent: true }) },
          { label: 'Folder', onSelect: () => newFolder() },
          { label: 'Group chat', onSelect: () => setS({ groupDialog: 'new' }) },
        ]}
        trigger={() => (
          <Btn icon={Plus}>
            <span className="hidden md:inline">New</span>
          </Btn>
        )}
      />

      <div className={cx('ml-auto shrink-0 items-center pl-2', agent || group ? 'flex' : 'hidden')}>
        <Btn
          icon={RotateCw}
          gap={10}
          iconSize={14}
          className="hidden xl:flex"
          onClick={() => {
            if (session) openSession(id, null)
            window.setTimeout(() => fillComposer(''), 0)
          }}
        >
          Start from scratch
        </Btn>
        <IconBtn icon={RotateCw} size={14} label="Start from scratch" className="xl:hidden" onClick={() => openSession(id, null)} />
        <Btn icon={MessageSquareDashed} className="ml-[12px] hidden 2xl:flex" title="Talk without leaving a trace: what is known is used, nothing new is remembered" onClick={() => newTemp(agent ? [agent.id] : (group?.members ?? []))}>
          Temporary
        </Btn>
        <IconBtn icon={MessageSquareDashed} label="Temporary chat: nothing is remembered" className="2xl:hidden" onClick={() => newTemp(agent ? [agent.id] : (group?.members ?? []))} />
        <Btn icon={Nut} className="ml-[12px] hidden xl:flex" onClick={settings}>
          Settings
        </Btn>
        <IconBtn icon={Nut} label={group ? 'Group chat settings' : 'Settings'} className="xl:hidden" onClick={settings} />
      </div>
    </div>
  )
}

/* ── the command palette ─────────────────────────────────────────────── */

function Palette() {
  const open = useDemo((s) => s.palette)
  const agents = useDemo((s) => s.agents)
  const setS = useDemo((s) => s.set)
  const [q, setQ] = useState('')
  const [at, setAt] = useState(0)
  const input = useRef<HTMLInputElement>(null)
  const all = useMemo(() => {
    const st = useDemo.getState()
    const who = (id: string | null) => st.agents.find((a) => a.id === id)
    return [
      ...agents.map((a) => ({ label: `Ask ${a.name}`, hint: a.hidden ? 'hidden' : a.label, icon: <Glyph palette={a.palette} />, run: () => st.openAgent(a.id) })),
      ...st.groups.map((g) => ({ label: `Ask ${g.name}`, hint: `${g.members.length} agents`, icon: <I icon={Users} className="text-ws-icon" />, run: () => st.openAgent(g.id) })),
      ...(Object.keys(TITLES) as View[]).map((v) => ({ label: `Go to ${TITLES[v].label}`, hint: '', icon: <I icon={TITLES[v].icon} className="text-ws-icon" />, run: () => st.go(v) })),
      { label: 'New temporary chat', hint: 'nothing is remembered', icon: <I icon={Burst} className="text-ws-icon" />, run: () => st.newTemp() },
      { label: 'New agent', hint: '', icon: <I icon={Plus} className="text-ws-icon" />, run: () => st.set({ addAgent: true }) },
      // conversations come up only once you type: by their title or by anything said in them
      ...st.sessions.map((c) => ({ label: c.title, hint: who(c.agentId)?.name ?? st.groups.find((g) => g.id === c.agentId)?.name ?? '', text: c.preview, deep: true, icon: who(c.agentId) ? <Glyph palette={who(c.agentId)!.palette} /> : <I icon={Users} className="text-ws-icon" />, run: () => c.agentId && st.openSession(c.agentId, c.id) })),
      { label: 'New folder', hint: '', icon: <I icon={Plus} className="text-ws-icon" />, run: () => newFolder() },
      { label: 'New group chat', hint: '', icon: <I icon={Plus} className="text-ws-icon" />, run: () => st.set({ groupDialog: 'new' }) },
    ]
  }, [agents, open]) // eslint-disable-line react-hooks/exhaustive-deps
  const needle = q.trim().toLocaleLowerCase('tr')
  const list = all.filter((x) => (needle ? `${x.label} ${x.hint} ${'text' in x ? x.text : ''}`.toLocaleLowerCase('tr').includes(needle) : !('deep' in x))).slice(0, 40)
  useEffect(() => {
    if (open) {
      setQ('')
      setAt(0)
      window.setTimeout(() => input.current?.focus(), 30)
    }
  }, [open])
  if (!open) return null
  const close = () => setS({ palette: false })
  const pick = (i: number) => {
    const it = list[i]
    if (!it) return
    close()
    it.run()
  }
  return (
    <div className="ws fixed inset-0 z-50 flex items-start justify-center px-3 pt-[12vh]">
      <div className="absolute inset-0 animate-ws-fade bg-black/70" onClick={close} />
      <div className="relative w-full max-w-[560px] animate-ws-pop overflow-hidden rounded-[10px] border border-[#2a2a2a] bg-[#111] shadow-[0_24px_64px_rgba(0,0,0,0.7)]">
        <div className="flex h-12 items-center gap-3 border-b border-ws-line px-4">
          <I icon={Search} className="text-ws-icon" />
          <input
            ref={input}
            value={q}
            onChange={(e) => {
              setQ(e.target.value)
              setAt(0)
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') (e.preventDefault(), setAt((i) => Math.min(list.length - 1, i + 1)))
              else if (e.key === 'ArrowUp') (e.preventDefault(), setAt((i) => Math.max(0, i - 1)))
              else if (e.key === 'Enter') pick(at)
              else if (e.key === 'Escape') close()
            }}
            placeholder="Search agents, conversations, pages…"
            className="h-full min-w-0 flex-1 tracking-normal"
            aria-label="Search"
          />
          <KeyCap>esc</KeyCap>
        </div>
        <div className="max-h-[52vh] overflow-y-auto p-1">
          {list.map((it, i) => (
            <button key={`${it.label}${i}`} type="button" onMouseEnter={() => setAt(i)} onClick={() => pick(i)} className={cx('flex h-9 w-full items-center gap-3 rounded-[8px] px-3 text-left', i === at ? 'bg-ws-raise text-ws-fg' : 'text-ws-fg-2')}>
              {it.icon}
              <span className="shrink-0">{it.label}</span>
              <span className="min-w-0 flex-1 truncate text-right text-[12px] tracking-normal text-ws-fg-3">{it.hint}</span>
            </button>
          ))}
          {!list.length && <p className="m-0 px-3 py-6 text-center text-[12px] tracking-normal text-ws-fg-3">Nothing matches “{q}”.</p>}
        </div>
      </div>
    </div>
  )
}

function Toast() {
  const toast = useDemo((s) => s.toast)
  if (!toast) return null
  return (
    <div key={toast.id} className="ws pointer-events-none fixed inset-x-0 bottom-[max(20px,env(safe-area-inset-bottom))] z-[60] flex justify-center px-4">
      <div className="animate-ws-in rounded-[8px] border border-[#2a2a2a] bg-[#161616] px-3 py-2 text-[13px] leading-[18px] tracking-normal text-ws-fg shadow-[0_12px_32px_rgba(0,0,0,0.6)]">{toast.text}</div>
    </div>
  )
}

/* ── the screen ──────────────────────────────────────────────────────── */

const VIEW: Record<View, [bar: () => ReactNode, page: () => ReactNode]> = {
  agents: [() => <AgentTabs />, () => <AgentsView />],
  chats: [() => <ChatsBar />, () => <ChatsView />],
  portfolio: [() => <PortfolioBar />, () => <PortfolioView />],
  workers: [() => <WorkersBar />, () => <WorkersView />],
  apps: [() => <AppsBar />, () => <AppsView />],
  settings: [() => <SettingsBar />, () => <SettingsView />],
}

export function Shell() {
  const view = useDemo((s) => s.view)
  useMedia('(min-width: 768px)') // re-render when the layout class changes
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey
      if (mod && e.key.toLowerCase() === 'k') (e.preventDefault(), useDemo.getState().set({ palette: !useDemo.getState().palette }))
      else if (mod && e.key.toLowerCase() === 'y') (e.preventDefault(), useDemo.getState().go('agents'))
    }
    document.addEventListener('keydown', key)
    return () => document.removeEventListener('keydown', key)
  }, [])
  return (
    <div className="ws flex h-dvh w-full overflow-hidden bg-ws-bg">
      <Sidebar />
      <main className="flex min-w-0 flex-1 flex-col">
        <TopBar />
        {VIEW[view][0]()}
        <div className="flex min-h-0 flex-1">
          <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto">
            <Guard name={view}>{VIEW[view][1]()}</Guard>
          </div>
          <Inspector />
        </div>
      </main>
      <Palette />
      {/* still the older windows: a stock's details, the calculator an answer can open */}
      <div className="lb-legacy">
        <SymbolModal />
        <Calculator />
      </div>
      <AddAgent />
      <AddGroup />
      <AddSpace />
      <AskHost />
      <PopHost />
      <Toast />
    </div>
  )
}
